import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../../../../axiosConfig';
import axios from 'axios';
import styles from './ChatUrl.module.css';

const ChatUrl = ({ onVideoSelect, onClearVideos, videoList = [] }) => {
  const [customerRequest, setCustomerRequest] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [displayChatHistory, setDisplayChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const videoDataRef = useRef({}); 

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (loading) {
      return;
    }
    
    setLoading(true);
    
    try {
      const currentSource = axios.CancelToken.source();
      
      const response = await axiosInstance.post('/chat_find_youtube_url/', {
        customer_request: customerRequest,
        chat_history: chatHistory,
      }, {
        cancelToken: currentSource.token,
      });

      const assistantAnswer = Array.isArray(response.data) 
        ? `I found ${response.data.length} videos about your request` 
        : (response.data.message || 'Search completed');
      
      setChatHistory([
        ...chatHistory,
        { question: customerRequest },
        { answer: assistantAnswer },
      ]);
      
      setDisplayChatHistory([
        ...displayChatHistory,
        { role: 'user', content: customerRequest },
        { role: 'assistant', content: response.data },
      ]);
      
      setCustomerRequest('');
      setResponseMessage('');
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error('There was an error!', error);
        setResponseMessage(error.response ? error.response.data.error : "An error occurred");
      }
    }
    setLoading(false);
  };

  const handleChatButtonClick = () => {
    setShowChat(!showChat); 
    if (showChat) { 
      setChatHistory([]); 
      setDisplayChatHistory([]); 
      // Clear the videos in the parent component
      if (onClearVideos) {
        onClearVideos();
      }
    }
  };

  // Helper function to extract video ID from YouTube URL
  const getYouTubeID = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Handle adding video to analysis - wrapped in useCallback to avoid dependency issues
  const handleAddToAnalysis = useCallback((videoUrl, videoTitle) => {
    if (onVideoSelect) {
      onVideoSelect(videoUrl, videoTitle);
    }
  }, [onVideoSelect]);

  const formatMessage = (message) => {
    if (typeof message === 'object' && message.videos) {
      let formatted = message.message ? `<p><b>${message.message}</b></p>` : '';
      
      message.videos.forEach((video, index) => {
        const videoId = getYouTubeID(video.Url);
        const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : '';
        const videoKey = `videos-${videoId || index}`;
        
        // Store video data in ref
        videoDataRef.current[videoKey] = {
          url: video.Url,
          title: video.Title
        };
        
        const isAdded = videoList.some(v => v.url === video.Url);
        
        formatted += `
          <div class="${styles.videoCard}">
            <div class="${styles.videoHeader}">
              ${thumbnailUrl ? `
                <div class="${styles.thumbnailContainer}" onclick="this.innerHTML='<iframe src=\\'https://www.youtube.com/embed/${videoId}?autoplay=1&controls=1\\' frameborder=\\'0\\' allow=\\'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture\\' allowfullscreen></iframe>'">
                  <img src="${thumbnailUrl}" alt="${video.Title}" class="${styles.thumbnail}" loading="lazy" />
                  <div class="${styles.playButton}">
                    <span class="${styles.playIcon}">▶</span>
                  </div>
                </div>
              ` : ''}
              <div class="${styles.videoInfo}">
                <p class="${styles.videoTitle}"><b>${index + 1}. ${video.Title}</b></p>
                <p class="${styles.videoMeta}"><b>Channel:</b> ${video.Channel}</p>
                <p class="${styles.videoMeta}"><b>Duration:</b> ${video.Duration} | <b>Published:</b> ${video.Publish_date}</p>
              </div>
            </div>
            <p class="${styles.videoDescription}"><b>Description:</b> ${video.Description}</p>
            <div class="${styles.videoActions}">
              <button 
                id="add-btn-videos-${videoId || index}"
                onclick='window.addVideoToAnalysis("videos-${videoId || index}")' 
                class="${styles.addButton}"
                ${isAdded ? 'disabled' : ''}
              >
                ${isAdded ? '✓ Added' : '➕ Add to Analysis'}
              </button>
            </div>
          </div>
        `;
      });
      
      return formatted;
    }
    
    if (Array.isArray(message)) {
      let formatted = '<p><b>Here are the videos I found:</b></p>';
      
      message.forEach((video, index) => {
        const videoId = getYouTubeID(video.Url);
        const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : '';
        const videoKey = `array-${videoId || index}`;
        
        // Store video data in ref
        videoDataRef.current[videoKey] = {
          url: video.Url,
          title: video.Title
        };
        
        const isAdded = videoList.some(v => v.url === video.Url);
        
        formatted += `
          <div class="${styles.videoCard}">
            <div class="${styles.videoHeader}">
              ${thumbnailUrl ? `
                <div class="${styles.thumbnailContainer}" onclick="this.innerHTML='<iframe src=\\'https://www.youtube.com/embed/${videoId}?autoplay=1&controls=1\\' frameborder=\\'0\\' allow=\\'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture\\' allowfullscreen></iframe>'">
                  <img src="${thumbnailUrl}" alt="${video.Title}" class="${styles.thumbnail}" loading="lazy" />
                  <div class="${styles.playButton}">
                    <span class="${styles.playIcon}">▶</span>
                  </div>
                </div>
              ` : ''}
              <div class="${styles.videoInfo}">
                <p class="${styles.videoTitle}"><b>${index + 1}. ${video.Title}</b></p>
                <p class="${styles.videoMeta}"><b>Channel:</b> ${video.Channel}</p>
                <p class="${styles.videoMeta}"><b>Duration:</b> ${video.Duration} | <b>Published:</b> ${video.Publish_date}</p>
              </div>
            </div>
            <p class="${styles.videoDescription}"><b>Description:</b> ${video.Description}</p>
            <div class="${styles.videoActions}">
              <button 
                id="add-btn-array-${videoId || index}"
                onclick='window.addVideoToAnalysis("array-${videoId || index}")' 
                class="${styles.addButton}"
                ${isAdded ? 'disabled' : ''}
              >
                ${isAdded ? '✓ Added' : '➕ Add to Analysis'}
              </button>
            </div>
          </div>
        `;
      });
      
      return formatted;
    }
    
    if (typeof message === 'string') {
      return message;
    }
    
    return JSON.stringify(message, null, 2);
  };

  // Set up global function for button clicks
  useEffect(() => {
    window.addVideoToAnalysis = (videoKey) => {
      try {
        const videoData = videoDataRef.current[videoKey];
        if (!videoData) {
          console.warn('Video data not found for key:', videoKey);
          setErrorMessage('Unable to add this video. Please try again.');
          setTimeout(() => setErrorMessage(''), 3000);
          return;
        }
        
        // Update button appearance immediately
        const button = document.getElementById(`add-btn-${videoKey}`);
        if (button && !button.disabled) {
          handleAddToAnalysis(videoData.url, videoData.title);
          button.textContent = '✓ Added';
          button.classList.add(styles.addButton);
          button.disabled = true;
        }
      } catch (error) {
        console.error('Error adding video to analysis:', error);
        setErrorMessage('Unable to add this video. Please try again.');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    };
    
    return () => {
      delete window.addVideoToAnalysis;
    };
  }, [handleAddToAnalysis]);

  return (
    <div>
      <button onClick={handleChatButtonClick} className={styles.chatButton}>
        Find Videos
      </button>
      
      {errorMessage && (
        <div className={styles.errorMessage}>
          ⚠️ {errorMessage}
        </div>
      )}
      
      {showChat && ( 
        <>
          <div className={styles.chatContainer}>
          
            {displayChatHistory.map((message, index) => (
              <div key={index} className={styles.messageContainer}>
                <strong>{message.role === 'user' ? 'You: ' : 'Assistant: '}</strong>
                {message.role === 'user' ? (
                  message.content
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }} />
                )}
              </div>
            ))}

            <form onSubmit={handleSubmit}>
              <label>
                <textarea 
                  value={customerRequest} 
                  onChange={e => { setCustomerRequest(e.target.value); setResponseMessage(''); }}  
                  placeholder="Search videos or ask follow-up questions..."
                  rows="4" 
                  className={styles.textArea}
                />
              </label>
              <input type="submit" value="Go" disabled={loading} />
            </form>

            {loading && <p>Loading...</p>}
          </div>
          <pre>{responseMessage}</pre>
        </>
      )}
    </div>
  );
};

export default ChatUrl;